import React, { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import PrismExportSignersModal from 'app/shared/components/forms/PrismExportSignersModal';
import _ from 'lodash';

import { getFileDateTimeName } from 'utils/file';
import { downloadOrShareBlob } from 'utils/nativeFile';

import { exportPrismFormPdfAction } from 'store/forms/actions';
import { openDialogAction } from 'store/global/actions';

import { getIsAllBorrowersSignerPresent } from '../../FormBuilder.utils';

const usePrismFormExport = ({ formId, formName, prismEditorRef, setIsLoading }) => {
  const dispatch = useDispatch();
  const isExportingRef = useRef(false);

  const runExport = useCallback(
    exportRequest => {
      if (isExportingRef.current) {
        return;
      }

      isExportingRef.current = true;
      setIsLoading(true);

      return dispatch(exportPrismFormPdfAction({ formId, ...exportRequest }))
        .then(blob =>
          downloadOrShareBlob(blob, getFileDateTimeName({ fileName: formName, extension: 'pdf' })),
        )
        .catch(Error)
        .finally(() => {
          isExportingRef.current = false;
          setIsLoading(false);
        });
    },
    [dispatch, formId, formName, setIsLoading],
  );

  return useCallback(
    ({ margins, pageNumbering, watermark }) => {
      const document = prismEditorRef.current?.getDocumentJson();

      if (_.isNil(document)) {
        return;
      }

      if (!getIsAllBorrowersSignerPresent(document)) {
        runExport({ document, margins, pageNumbering, watermark });

        return;
      }

      dispatch(
        openDialogAction({
          header: 'Specify the number of signers',
          body: dialogProps => (
            <PrismExportSignersModal
              {...dialogProps}
              document={document}
              margins={margins}
              pageNumbering={pageNumbering}
              watermark={watermark}
              onSubmit={runExport}
            />
          ),
        }),
      );
    },
    [dispatch, prismEditorRef, runExport],
  );
};

export default usePrismFormExport;
